import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Paver patio, 300 sq ft: excavate 7 in, geotextile, compacted base, bedding sand, concrete pavers', amount: '$6,900' },
  { item: 'New planting bed along the patio, 120 sq ft: 9 shrubs, 3 in hardwood mulch', amount: '$1,450' },
  { item: 'Remove old sod and debris, two dump-trailer loads', amount: '$350' },
  { item: 'Optional: four low-voltage path lights with transformer', amount: '$780' },
];

const faq = [
  {
    q: 'Can my estimator see the whole company\'s numbers?',
    a: 'No. In Scopie an estimator writes quotes and visits clients for assessments, and sees only their own quotes. Invoices, payments and other people\'s quotes stay with the owner and the office.',
  },
  {
    q: 'How should a landscaping quote handle materials price changes?',
    a: 'Give it a validity date and say how long material prices are held. For larger hardscape jobs, many contractors collect a deposit before ordering stone; put your deposit rule on the quote so it is signed with everything else.',
  },
  {
    q: 'Can the homeowner add lighting or extra beds after seeing the price?',
    a: 'Yes. Put the extras as optional lines. The homeowner ticks what they want on the quote page and the total updates before they sign, so phase two can be agreed on the spot or left for later.',
  },
  {
    q: 'Does Scopie handle weekly mowing contracts?',
    a: 'Not yet. Scopie has no recurring visits or recurring billing today. It is built for quoted design, build and cleanup work.',
  },
];

const page: IndustryPage = {
  slug: 'landscaping',
  order: 3,
  group: 'Outdoor',
  tile: 'landscaping',
  path: '/for/landscaping',
  primaryKeyword: 'landscaping business software',
  secondaryKeywords: ['landscaping estimate software', 'hardscape quote', 'landscape estimator app'],
  title: 'Landscaping Software for Quotes & Invoices | Scopie', // 51
  metaDescription:
    'Book the site visit, quote patios, beds and cleanups line by line, offer lighting as an extra, and keep estimators to their own quotes. For landscapers.', // 151
  h1: 'Landscape and hardscape quotes, from site visit to signed',
  answerFirst:
    'Scopie fits landscaping companies that win work on site: book the assessment visit, price the base, pavers, plants and haul-away as separate lines, and offer lighting as an extra. Estimators see only their own quotes. Once the homeowner signs, the job is scheduled with the crew and the skid steer or dump trailer it needs.',
  exampleQuote: { label: 'Sample patio and planting quote', lines, total: '$9,480', depositNote: 'Material deposit and plant warranty wording are the contractor\'s own clauses.' },
  blocks: [
    {
      type: 'list',
      id: 'flow',
      h2: 'How a landscaping job moves through Scopie',
      style: 'steps',
      items: [
        { title: 'Request', text: 'The homeowner calls about a patio. You log it with the address and what they have in mind.' },
        { title: 'Assessment visit', text: 'Book the site visit on the schedule and assign it to yourself or an estimator.' },
        { title: 'Quote', text: 'Price excavation, base, pavers, beds and haul-away as lines; add lighting or a seat wall as options.' },
        { title: 'Signed', text: 'Text the link. The homeowner chooses extras, reads your terms and signs.' },
        { title: 'Job', text: 'Schedule the build with the crew and the machines, then share the invoice link when it is finished.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A patio and planting bed, priced in parts',
      label: 'Sample quote: backyard patio with new bed',
      lines,
      total: '$9,480',
      totalLabel: 'Total if the path lights are added',
      note: 'Example figures. The lighting is an optional line the homeowner can tick or save for later.',
    },
    {
      type: 'text',
      id: 'estimators',
      h2: 'Estimators who quote without seeing the books',
      lead: 'Your estimator needs to price jobs, not see what the whole company billed last month.',
      paragraphs: [
        'Growing landscape companies send estimators out on site visits. In Scopie an estimator has their own role: they write quotes and run assessment visits, and they see only the quotes they own. Each quote carries its owner, which makes it easy to see who sold what.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Hardscape quoting mistakes that eat the margin',
      style: 'mistakes',
      items: [
        { title: 'Base depth not written', text: 'Four inches and seven inches of base are very different jobs. Put the depth on the line.' },
        { title: 'Access ignored', text: 'No gate wide enough for the skid steer means wheelbarrows and days of labor.' },
        { title: 'Plant warranty unclear', text: 'Say how long plants are covered and what voids it, such as no watering.' },
        { title: 'Utilities and irrigation assumed', text: 'Locates and sprinkler repairs belong in the exclusions or on their own line.' },
        { title: 'Stone ordered before a deposit', text: 'Pallets of pavers are expensive to store. Get the deposit rule signed first.' },
        { title: 'Scope changes agreed verbally', text: 'A bigger patio is a new price on paper, not a nod on site.' },
      ],
    },
    {
      type: 'text',
      id: 'season',
      h2: 'Spring builds, fall cleanups',
      paragraphs: [
        'Design and build work stacks up in early spring, when everyone wants the patio ready by Memorial Day, and again in fall for planting and cleanups. Quotes that are signed quickly claim the best weeks, and the crew calendar shows which machine is out where. Crew members see the job and the address, never the price.',
        'Clients come back for phase two. Each one stays on your client list with the quote they signed, so next year\'s lighting or seat wall builds on what is already there.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Landscaping quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/tree-service', anchor: 'Scopie for tree service companies', status: 'live' },
    { href: '/templates/landscaping-quote-template', anchor: 'free landscaping quote template', status: 'live' },
  ],
  cta: {
    heading: 'Win the spring builds before they go to bid',
    text: 'Set up your hardscape items, plant lists and terms in Scopie, and your estimators can quote from the site.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
